import {
  Select,
  SelectProps,
  styled,
  TextInput,
  TextInputProps,
} from '@zeiss/beyond-online-react';
import { trackEvent } from '@zeiss/lib-helpers';
import * as React from 'react';

import { countries, Country, unavailableIsoCodes } from './countries';
import { isInvalidPhoneNumber } from './utils';
import { useTrackingDetails } from 'src/components/Tracking/TrackingDetails';
import { useGlobalConfig } from 'src/config/GlobalConfig';
import { useLocations } from 'src/hooks';

// eslint-disable-next-line react-refresh/only-export-components
export const DEFAULT_INVALID_FORMAT_MESSAGE =
  'Please enter a valid phone number.';

export interface PhoneInputProps {
  /**
   * The label to apply to the country select element
   *
   * @defaultValue 'Location'
   *
   * @remarks
   * The default value is set to 'Location' in line with ZEISS' naming guidelines
   * See {@link https://dev.azure.com/ZEISSgroup/ZEISS%20ID/_wiki/wikis/ZEISS-ID.wiki/72527/07-_-Legal}
   *
   */
  countryLabel?: string;
  /**
   * The default selected country (dialing code with '+' prefix)
   *
   * For instance if pre-selecting the user's country by Geolocation.
   *
   * @defaultValue '+49'
   */
  defaultCountry?: string;
  /**
   * Applies the error styles to the input. If it is a string, the provided
   * error message is displayed.
   */
  error?: TextInputProps['error'];
  /**
   * The message to display when the user enters a value in the digit part that
   * doesn't meet the basic validation criteria
   *
   * @remarks
   * See `isInValidPhoneNumber` for details about the validation
   *
   * @defaultValue {@link DEFAULT_INVALID_FORMAT_MESSAGE}
   */
  invalidFormatMessage?: string;
  /**
   * "ISO 3166-1 alpha-2" code for the language list
   *
   * @defaultValue 'EN'
   */
  language?: string;
  /**
   * Callback with the computed phone number provided as value.
   */
  onChange?: (value: string) => void;
  /**
   * The label to apply to the phone input element
   *
   * @defaultValue 'Phone number'
   */
  phoneLabel?: string;
  placeholder?: string;
}

const SelectContainer = styled.div`
  position: relative;

  /* Positioned over select text and displays the selected dialcode (e.g +49) */
  [data-testid='DialCode'] {
    position: absolute;
    top: 0;
    line-height: 72px;
    pointer-events: none;
  }

  /* Hide select text since the options are in the format "<country>: <country-code>" (e.g Germany: +49) */
  button > span {
    display: none;
  }
`;

const Container = styled.div.attrs(({ ref }) => ({ ref }))`
  display: flex;
  gap: var(--spacing-xs);

  ${SelectContainer} {
    width: 148px;
  }

  /* Beyond now wraps the text input in an additional "aria" div. This applies to the text input wrapper */
  & > :not([data-testid='SelectContainer']) {
    flex-grow: 1;
  }
`;

const getFilteredCountries = (supportedLocations: string[]) => {
  // Filter out countries that are not in the supported locations list
  const supportedCountries =
    supportedLocations.length > 0
      ? countries.filter((country) =>
          supportedLocations
            .map((loc) => loc.toUpperCase())
            .includes(country.iso2.toUpperCase()),
        )
      : countries;

  // Filter out countries that are not supported by ESB
  const filteredCountries = supportedCountries.filter(
    (item) => !unavailableIsoCodes.includes(item.iso2.toUpperCase()),
  );

  /**
   * Mapping of dialCode (with '+' prefix, e.g +49) to filtered country data
   */
  const countriesByDialCode: Record<string, Country> = filteredCountries.reduce(
    (acc: Record<string, Country>, curr) => {
      acc[`+${curr.dialCode}`] = curr;
      return acc;
    },
    {},
  );

  /**
   * Mapping of ISO code(alpha 2 e.g DE) to filtered country data
   */
  const countriesByIsoCode: Record<string, Country> = filteredCountries.reduce(
    (acc: Record<string, Country>, curr) => {
      acc[curr.iso2] = curr;
      return acc;
    },
    {},
  );

  return { countriesByDialCode, countriesByIsoCode };
};

//TODO: Further development is planned to replace two components (Select, TextInput) with a single component with one label.

/**
 * Phone input component.
 *
 * If we eventually need to move this component to Beyond then it makes more sense
 * to implement it using existing phone input libraries that are more robust/battle tested
 * to handle validation and support the best user experience.
 */
export const PhoneInput = ({
  countryLabel = 'Location',
  defaultCountry = '+49',
  error,
  invalidFormatMessage = DEFAULT_INVALID_FORMAT_MESSAGE,
  language = 'EN',
  onChange,
  phoneLabel = 'Phone number',
  placeholder,
}: PhoneInputProps) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const { defaultLanguage, journey, productTitle, stage } =
    useTrackingDetails();

  const { supportedLocations } = useGlobalConfig();
  const { countriesByDialCode, countriesByIsoCode } =
    getFilteredCountries(supportedLocations);

  // If the defaultCountry is invalid(or not in our country data) we default to Germany
  const defaultCountryISO = countriesByDialCode[defaultCountry]?.iso2 ?? 'de';
  const [countryCode, setCountryCode] = React.useState(defaultCountryISO);
  const [phoneDigits, setPhoneDigits] = React.useState('');

  const { locations } = useLocations({ language });

  /* Focus phone input to show placeholder immediately. */
  React.useEffect(() => {
    inputRef.current?.focus();
  }, []);

  /**
   * Mapping of ISO code (alpha 2 e.g DE) to location (from locations API)
   */
  const locationsByIsoCode = React.useMemo(() => {
    return locations.reduce(
      (acc: Record<string, (typeof locations)[number]>, curr) => {
        // convert to lower case because country data has it lower cased
        acc[curr.id.toLowerCase()] = curr;
        return acc;
      },
      {},
    );
  }, [locations]);

  const options: SelectProps['options'] = React.useMemo(() => {
    return locations
      .map(({ id }) => {
        // convert to lower case because country data has it lower cased
        const countryCode = id.toLowerCase();

        const data = countriesByIsoCode[countryCode];
        const location = locationsByIsoCode[countryCode];

        /**
         *
         * Some locations from the location API don't have a corresponding value
         * in the filtered country data list.
         *
         * We set the label to `UNKNOWN` to use to filter them out
         */
        const label = data ? `${location.name}: +${data.dialCode}` : 'UNKNOWN';

        return {
          key: countryCode,
          label,
        };
      })
      .filter(({ label }) => label !== 'UNKNOWN');
  }, [locations]);

  const handlePhoneDigitChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = event.currentTarget;

    setPhoneDigits(value);

    const selectedCountry = countriesByIsoCode[countryCode];

    const dialCode = selectedCountry.dialCode;

    onChange?.(value.length ? `+${dialCode}${value}` : '');
  };

  const handleCountrySelect = (option: string) => {
    setCountryCode(option);

    inputRef.current?.focus();

    const selectedCountry = countriesByIsoCode[option];

    const dialCode = selectedCountry.dialCode;

    onChange?.(phoneDigits.length ? `+${dialCode}${phoneDigits}` : '');
  };

  const getErrorMessage = () => {
    if (error) return error;

    if (isInvalidPhoneNumber(phoneDigits)) return invalidFormatMessage;

    return undefined;
  };

  const errorMessage = getErrorMessage();

  const selectedDialcode = countriesByIsoCode[countryCode].dialCode;

  /**
   * @todo: Snowflake cannot be shared with the tracking built-in. We should
   * make it an optional prop that can be used instead.
   */
  const triggerEventTracking = (input: 'PhoneLocation' | 'PhoneNumber') => {
    trackEvent({
      properties: {
        contentHierarchy1: productTitle,
        contentHierarchy2: journey,
        contentHierarchy3: stage,
        defaultLanguage,
        eventAction: 'Click',
        eventName: 'CTA',
        eventValue: input,
      },
    });
  };

  return (
    <Container>
      <SelectContainer data-testid="SelectContainer">
        <Select
          error={errorMessage ? true : undefined}
          label={countryLabel}
          onChange={handleCountrySelect}
          onFocus={() => triggerEventTracking('PhoneLocation')}
          options={options}
          value={countryCode}
        />

        <span
          aria-hidden="true"
          data-testid="DialCode"
        >{`+${selectedDialcode}`}</span>
      </SelectContainer>

      <TextInput
        error={errorMessage}
        inputMode="numeric"
        label={phoneLabel}
        onChange={handlePhoneDigitChange}
        onFocus={() => triggerEventTracking('PhoneNumber')}
        pattern="[0-9]*"
        placeholder={placeholder}
        ref={inputRef}
        value={phoneDigits}
      />
    </Container>
  );
};
